import { createFileRoute } from '@tanstack/react-router';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { PageContent } from '@/components/page-content';
import { PageHeader } from '@/components/page-header';
import { DatePicker } from '@/components/date-picker';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { SearchPerformanceReport } from '@/components/reports/search-performance-report';
import { useDateRange } from '@/hooks/use-date-range';
import { useFeatures } from '@/hooks/use-features';

// Google's most recent days are still being processed, and a 25,000-row cap
// spread over a year leaves the breakdowns very thin, so shorter ranges only.
const SEARCH_CONSOLE_RANGES = [
  'Last 7 Days',
  'Last 30 Days',
  'Last 3 Months',
  'Last 6 Months',
];

export const Route = createFileRoute('/_auth/_side-layout/search-console')({
  component: SearchConsoleReportPage,
});

const isoDay = (date: Date): string => date.toISOString().slice(0, 10);

// Flag-off placeholder, matching the Signals feed's "coming soon" state
// (super-admins enable the org's `searchConsole` flag to see the real report).
function ComingSoonState() {
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <MagnifyingGlassIcon />
        </EmptyMedia>
        <EmptyTitle>Search: Coming Soon</EmptyTitle>
        <EmptyDescription>
          Google Search Console clicks, impressions, and position joined to the
          companies that landed on each page, so a search query stops being an
          anonymous keyword and becomes an account you can sell into.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function SearchConsoleReportPage() {
  const [dateRange] = useDateRange();
  const { isEnabled } = useFeatures();
  const enabled = isEnabled('searchConsole');

  return (
    <>
      <PageHeader
        title="Search"
        isNew
        description="Which search queries brought companies to your site."
        actions={
          enabled ? (
            <DatePicker allowedRanges={SEARCH_CONSOLE_RANGES} />
          ) : undefined
        }
      />
      <PageContent>
        {enabled ? (
          <SearchPerformanceReport
            startDate={isoDay(dateRange.startDate)}
            endDate={isoDay(dateRange.endDate)}
          />
        ) : (
          <ComingSoonState />
        )}
      </PageContent>
    </>
  );
}
